import { API_NOT_JSON, isProblem } from '@/shared/api/problem'

export const ERROR_KINDS = [
  'network',
  'server',
  'app',
  'stale-build',
  'not-found',
  'forbidden',
  'api-misrouted',
] as const

export type ErrorKind = (typeof ERROR_KINDS)[number]

const STALE_BUILD = /dynamically imported module|Importing a module script failed|ChunkLoadError/i

/**
 * Как выглядит обрыв сети в трёх движках. Формулировки разные, и «load failed» — не описка:
 * именно так Safari сообщает о сетевом сбое в `fetch`.
 *
 *   Chrome  → TypeError: Failed to fetch
 *   Firefox → TypeError: NetworkError when attempting to fetch resource.
 *   Safari  → TypeError: Load failed
 *
 * Без последнего варианта сетевой сбой в Safari уезжал бы в 'app': экран смерти сказал бы
 * «что-то сломалось» вместо «нет связи», а слой данных не стал бы повторять запрос.
 */
const NETWORK = /fetch|network|load failed/i

export function classifyError(error: unknown): ErrorKind {
  if (error instanceof Error && STALE_BUILD.test(error.message)) return 'stale-build'

  if (isProblem(error)) {
    // Проверяем ДО статуса: наш синтетический Problem несёт 502, и без этой ветки он уехал бы
    // в 'server' — «сервер не смог ответить, это не ваша ошибка». А ошибка как раз ваша: /api
    // не проксируется на gateway. Правильный текст экономит часы на первом же деплое.
    if (error.type === API_NOT_JSON) return 'api-misrouted'

    if (error.status === 404) return 'not-found'
    if (error.status === 403) return 'forbidden'
    if (error.status >= 500) return 'server'
    return 'app'
  }

  if (error instanceof TypeError && NETWORK.test(error.message)) return 'network'
  if (!navigator.onLine) return 'network'

  return 'app'
}

export function humanMessage(kind: ErrorKind): { title: string; hint: string } {
  switch (kind) {
    case 'network':
      return {
        title: 'Не получается связаться с сервером',
        hint: 'Похоже, пропал интернет. Проверьте соединение — данные подтянутся сами.',
      }
    case 'server':
      return {
        title: 'Сервер не смог ответить',
        hint: 'Это не ваша ошибка. Мы уже знаем о ней. Попробуйте обновить страницу через минуту.',
      }
    case 'stale-build':
      return {
        title: 'Приложение обновилось',
        hint: 'Вышла новая версия, а у вас открыта старая. Обновите страницу — всё встанет на место.',
      }
    case 'not-found':
      return {
        title: 'Здесь ничего нет',
        hint: 'Похоже, это удалили или ссылка неверная.',
      }
    case 'forbidden':
      return {
        title: 'Нет доступа',
        hint: 'У вашей роли нет прав на этот раздел. Попросите администратора компании.',
      }
    case 'api-misrouted':
      // Единственное сообщение, которое говорит с тем, кто ДЕПЛОИЛ, а не с тем, кто работает:
      // случиться оно может только при неверной раздаче, и обновление страницы тут не поможет.
      // Общее «что-то сломалось» отправило бы человека искать причину не там.
      return {
        title: 'Сервер ответил не тем',
        hint: 'Вместо данных пришла веб-страница. Обычно так бывает, когда /api не проксируется на gateway — проверьте конфигурацию раздачи.',
      }
    case 'app':
      return {
        title: 'Что-то сломалось',
        hint: 'Мы не ожидали такого поворота. Обновите страницу — скорее всего, всё заработает.',
      }
  }
}
